/**
 * Zitadel OIDC PKCE client for Laabha.
 *
 * Drives the OIDC Authorization Code + PKCE flow against
 * `https://dev-auth.axonbos.com` (Zitadel) for the `laabha-dev` OIDC app.
 *
 * Endpoints (per the OIDC discovery doc at https://dev-auth.axonbos.com/.well-known/openid-configuration):
 *   authorize: /oauth/v2/authorize
 *   token:     /oauth/v2/token
 *   userinfo:  /oidc/v1/userinfo
 *   end_session: /oidc/v1/end_session
 *
 * Why we don't use `oidc-client-ts`: the redirect handling and PKCE storage
 * are simple enough to inline here. Eliminates an external dependency and
 * keeps the bundle tiny.
 *
 * Storage keys (compatible with the rest of the AxonBOS stack):
 *   axon_token         — Zitadel access_token (JWT) — Bearer header
 *   axon_id_token      — Zitadel id_token (JWT)     — for /auth/me equivalent
 *   axon_refresh_token — Zitadel refresh_token
 *   axon_user          — cached user info from id_token
 *   pkce_verifier      — TEMPORARY between authorize redirect and callback
 *   oidc_state         — TEMPORARY anti-CSRF state
 */
import { setAuth } from './auth';

// Zitadel issuer (must match the `iss` claim in tokens).
// In production, swap `dev-auth` for `auth`.
export const ZITADEL_ISSUER = 'https://dev-auth.axonbos.com';
export const LAABHA_CLIENT_ID = '372805783957602315';

/** Compute the redirect URI from the current origin so dev/dev-yes/localhost all work. */
export function redirectUri(): string {
  return `${window.location.origin}/desktop/auth/callback`;
}

/** Compute the post-logout redirect from the current origin. */
export function postLogoutRedirectUri(): string {
  return `${window.location.origin}/desktop/`;
}

const SCOPE = 'openid profile email offline_access urn:zitadel:iam:org:project:id:zitadel:aud';

// ── PKCE primitives ──────────────────────────────────────────────────────────
function base64urlEncode(buf: ArrayBuffer): string {
  const bytes = new Uint8Array(buf);
  let str = '';
  for (let i = 0; i < bytes.length; i += 1) str += String.fromCharCode(bytes[i]);
  return btoa(str).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function randomB64Url(byteLength: number): string {
  const buf = new Uint8Array(byteLength);
  crypto.getRandomValues(buf);
  return base64urlEncode(buf.buffer);
}

async function sha256B64Url(s: string): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(s));
  return base64urlEncode(digest);
}

// ── Public API ───────────────────────────────────────────────────────────────

/**
 * Begin Zitadel login by redirecting the browser to the Zitadel /authorize endpoint.
 * The user types credentials in Zitadel's hosted UI; Zitadel handles email
 * verification, password reset, MFA, etc.
 */
export async function startZitadelLogin(): Promise<void> {
  const verifier = randomB64Url(32);
  const challenge = await sha256B64Url(verifier);
  const state = randomB64Url(16);
  const nonce = randomB64Url(16);

  sessionStorage.setItem('pkce_verifier', verifier);
  sessionStorage.setItem('oidc_state', state);
  sessionStorage.setItem('oidc_nonce', nonce);

  const params = new URLSearchParams({
    client_id: LAABHA_CLIENT_ID,
    redirect_uri: redirectUri(),
    response_type: 'code',
    scope: SCOPE,
    state,
    nonce,
    code_challenge: challenge,
    code_challenge_method: 'S256',
    prompt: 'login',
  });
  window.location.assign(`${ZITADEL_ISSUER}/oauth/v2/authorize?${params.toString()}`);
}

/**
 * Begin Zitadel registration — same redirect, but uses Zitadel's create-account
 * screen. Zitadel sends the email verification automatically.
 */
export async function startZitadelRegister(): Promise<void> {
  const verifier = randomB64Url(32);
  const challenge = await sha256B64Url(verifier);
  const state = randomB64Url(16);
  const nonce = randomB64Url(16);

  sessionStorage.setItem('pkce_verifier', verifier);
  sessionStorage.setItem('oidc_state', state);
  sessionStorage.setItem('oidc_nonce', nonce);

  const params = new URLSearchParams({
    client_id: LAABHA_CLIENT_ID,
    redirect_uri: redirectUri(),
    response_type: 'code',
    scope: SCOPE,
    state,
    nonce,
    code_challenge: challenge,
    code_challenge_method: 'S256',
    prompt: 'create',
  });
  window.location.assign(`${ZITADEL_ISSUER}/oauth/v2/authorize?${params.toString()}`);
}

interface ZitadelTokenResponse {
  access_token: string;
  id_token?: string;
  refresh_token?: string;
  token_type: string;
  expires_in: number;
}

interface ZitadelUserInfo {
  sub: string;
  email?: string;
  email_verified?: boolean;
  name?: string;
  given_name?: string;
  family_name?: string;
  preferred_username?: string;
}

/**
 * Handle Zitadel's /authorize callback (?code=…&state=…).
 * Exchanges the auth code for tokens, fetches userinfo, and persists everything
 * via the existing `setAuth` helper so the rest of the app sees a normal session.
 */
export async function handleZitadelCallback(searchParams: URLSearchParams): Promise<void> {
  const code = searchParams.get('code');
  const state = searchParams.get('state');
  const err = searchParams.get('error');
  const errDesc = searchParams.get('error_description');
  if (err) throw new Error(`Zitadel error: ${err}${errDesc ? ` — ${errDesc}` : ''}`);
  if (!code) throw new Error('Missing auth code in callback URL');
  const expectedState = sessionStorage.getItem('oidc_state');
  if (!state || state !== expectedState) throw new Error('Invalid OIDC state — possible CSRF replay');
  const verifier = sessionStorage.getItem('pkce_verifier');
  if (!verifier) throw new Error('Missing PKCE verifier — start the flow again');

  // Exchange code for tokens
  const body = new URLSearchParams({
    grant_type: 'authorization_code',
    code,
    redirect_uri: redirectUri(),
    client_id: LAABHA_CLIENT_ID,
    code_verifier: verifier,
  });

  const tokenResp = await fetch(`${ZITADEL_ISSUER}/oauth/v2/token`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: body.toString(),
  });
  if (!tokenResp.ok) {
    const t = await tokenResp.text();
    throw new Error(`Token exchange failed (HTTP ${tokenResp.status}): ${t}`);
  }
  const tokens = (await tokenResp.json()) as ZitadelTokenResponse;

  // Fetch user info using the access token
  const userResp = await fetch(`${ZITADEL_ISSUER}/oidc/v1/userinfo`, {
    headers: { Authorization: `Bearer ${tokens.access_token}` },
  });
  if (!userResp.ok) {
    throw new Error(`Userinfo failed (HTTP ${userResp.status})`);
  }
  const u = (await userResp.json()) as ZitadelUserInfo;

  // Persist via the existing setAuth helper so the rest of the app's auth
  // surface (api.ts, billing.ts, etc.) sees a normal Bearer token.
  setAuth(
    {
      access_token: tokens.access_token,
      refresh_token: tokens.refresh_token,
    },
    {
      id: u.sub,
      email: u.email || '',
      full_name: u.name || u.given_name || u.preferred_username,
      // company_id is resolved server-side via zitadel_user_id; not in id_token
    },
  );
  if (tokens.id_token) localStorage.setItem('axon_id_token', tokens.id_token);

  // Clean up PKCE / state from sessionStorage
  sessionStorage.removeItem('pkce_verifier');
  sessionStorage.removeItem('oidc_state');
  sessionStorage.removeItem('oidc_nonce');
}

/** Sign the user out: clear local state and redirect to Zitadel end_session. */
export function startZitadelLogout(): void {
  const idToken = localStorage.getItem('axon_id_token') || '';
  // Wipe local auth FIRST so even if Zitadel hop fails we're logged out locally
  ['axon_token', 'axon_refresh_token', 'axon_user', 'axon_company_id', 'axon_id_token'].forEach(
    (k) => localStorage.removeItem(k),
  );
  const params = new URLSearchParams({
    post_logout_redirect_uri: postLogoutRedirectUri(),
    client_id: LAABHA_CLIENT_ID,
  });
  if (idToken) params.set('id_token_hint', idToken);
  window.location.assign(`${ZITADEL_ISSUER}/oidc/v1/end_session?${params.toString()}`);
}
